import React, { useEffect, useState } from "react";
import { FileText, Plus, Save, Trash2, Send, Eye, X } from "lucide-react";
import { templatesApi, campaignsApi } from "../../api";
import { toast } from "sonner";

const STARTERS = [
  {
    name: "Featured Deal",
    category: "Deal",
    subject: "New Featured DSCR Deal — {{address}}",
    html: `<div style="background:#f4f4f4;padding:24px 0;font-family:Arial,Helvetica,sans-serif">
  <div style="max-width:600px;margin:0 auto;background:#ffffff;border:1px solid #e5e5e5;padding:32px">
    <div style="border-bottom:1px solid #000000;padding-bottom:14px;margin-bottom:24px">
      <span style="font-size:20px;font-weight:bold;letter-spacing:1px;color:#000000">DSCR DAILY</span>
    </div>
    <h1 style="font-size:19px;color:#000000;margin:0 0 16px">New Featured Deal</h1>
    <p style="color:#000000;font-size:15px;line-height:1.6">Hi there,</p>
    <p style="color:#000000;font-size:15px;line-height:1.6">We just added a cash-flowing property that qualifies for DSCR financing:</p>
    <div style="border:1px solid #e5e5e5;padding:20px;margin:18px 0">
      <h3 style="margin:0 0 8px;color:#000000">123 Example St, Tampa FL</h3>
      <p style="margin:4px 0;color:#333333">Price: <b>$285,000</b></p>
      <p style="margin:4px 0;color:#333333">Est. Rent: <b>$2,450/mo</b></p>
      <p style="margin:4px 0;color:#333333">DSCR: <b>1.28</b> &middot; Cash Flow: <b>+$420/mo</b></p>
    </div>
    <a href="https://dscrdaily.com" style="display:inline-block;background:#000000;color:#ffffff;padding:12px 22px;text-decoration:none;font-weight:bold;border-radius:2px">View Deal &amp; Get Terms</a>
    <div style="border-top:1px solid #e5e5e5;margin-top:32px;padding-top:16px;font-size:12px;color:#666666">DSCR Daily &middot; Reply to this email with questions.</div>
  </div>
</div>`,
  },
  {
    name: "Market Update",
    category: "Update",
    subject: "DSCR Market Update — What Investors Need to Know",
    html: `<div style="background:#f4f4f4;padding:24px 0;font-family:Arial,Helvetica,sans-serif">
  <div style="max-width:600px;margin:0 auto;background:#ffffff;border:1px solid #e5e5e5;padding:32px">
    <div style="border-bottom:1px solid #000000;padding-bottom:14px;margin-bottom:24px">
      <span style="font-size:20px;font-weight:bold;letter-spacing:1px;color:#000000">DSCR DAILY</span>
    </div>
    <h1 style="font-size:19px;color:#000000;margin:0 0 16px">Market Update</h1>
    <p style="color:#000000;font-size:15px;line-height:1.6">Hi there,</p>
    <p style="color:#000000;font-size:15px;line-height:1.6">Here's the latest on DSCR lending and rental markets this week:</p>
    <ul style="color:#000000;font-size:15px;line-height:1.8;padding-left:20px">
      <li>DSCR rates are currently averaging around 7.25%–7.75%.</li>
      <li>Cash-flow markets to watch: Florida, Texas, Georgia.</li>
      <li>We have new qualifying inventory added daily.</li>
    </ul>
    <a href="https://dscrdaily.com" style="display:inline-block;background:#000000;color:#ffffff;padding:12px 22px;text-decoration:none;font-weight:bold;border-radius:2px">Browse Live Deals</a>
    <div style="border-top:1px solid #e5e5e5;margin-top:32px;padding-top:16px;font-size:12px;color:#666666">DSCR Daily &middot; Reply anytime.</div>
  </div>
</div>`,
  },
];

const empty = { name: "", category: "General", subject: "", html: "" };

export default function TemplatesView() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState(empty);
  const [preview, setPreview] = useState(false);
  const [group, setGroup] = useState("subscribers");
  const [sending, setSending] = useState(false);

  const load = async () => { try { setList(await templatesApi.list()); } catch { toast.error("Failed to load templates"); } };
  useEffect(() => { load(); }, []);

  const editNew = (starter) => { setSel(null); setF(starter ? { ...starter } : { ...empty }); };
  const editExisting = (t) => { setSel(t.id); setF({ name: t.name, category: t.category, subject: t.subject, html: t.html }); };

  const save = async () => {
    if (!f.name || !f.subject || !f.html) { toast.error("Name, subject and body are required"); return; }
    try {
      if (sel) { await templatesApi.update(sel, f); toast.success("Template updated"); }
      else { const t = await templatesApi.create(f); setSel(t.id); toast.success("Template saved"); }
      load();
    } catch { toast.error("Could not save template"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await templatesApi.remove(id); toast("Template deleted");
    if (sel === id) { setSel(null); setF(empty); }
    load();
  };

  const send = async () => {
    if (!f.subject || !f.html) { toast.error("Fill subject and body first"); return; }
    const label = group === "all" ? "ALL contacts" : "subscribers";
    if (!window.confirm(`Send "${f.subject}" to ${label}?`)) return;
    setSending(true);
    try {
      const r = await campaignsApi.send({ subject: f.subject, html: f.html, group });
      toast.success(`Sent: ${r.sent} delivered, ${r.queued} queued (${r.recipients} recipients)`);
    } catch { toast.error("Send failed"); } finally { setSending(false); }
  };

  return (
    <div data-testid="templates-view" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: "1.4rem", alignItems: "start" }}>
      <div className="card" style={{ padding: "1.2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <span className="eyebrow" style={{ color: "var(--muted-2)" }}>Templates</span>
          <button className="btn-gold" data-testid="new-template-btn" style={{ padding: "0.35rem 0.7rem", fontSize: "0.7rem" }} onClick={() => editNew(null)}><Plus size={13} /> New</button>
        </div>
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {list.map((t) => (
            <div key={t.id} data-testid={`template-item-${t.id}`} onClick={() => editExisting(t)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.6rem 0.7rem", borderRadius: 6, cursor: "pointer", border: `1px solid ${sel === t.id ? "var(--gold)" : "rgba(255,255,255,0.08)"}`, background: sel === t.id ? "var(--gold-soft)" : "transparent" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem", color: sel === t.id ? "var(--gold)" : "var(--text)" }}><FileText size={14} /> {t.name}</span>
              <button onClick={(e) => { e.stopPropagation(); remove(t.id); }} style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer" }}><Trash2 size={13} /></button>
            </div>
          ))}
          {list.length === 0 && <p className="body-text" style={{ fontSize: "0.76rem" }}>No saved templates yet.</p>}
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: "1rem", paddingTop: "1rem" }}>
          <div style={{ fontSize: "0.62rem", letterSpacing: "0.14em", color: "var(--muted-2)", marginBottom: "0.6rem" }}>QUICK START</div>
          {STARTERS.map((s) => (
            <button key={s.name} data-testid={`starter-${s.name.replace(/\s/g, "-").toLowerCase()}`} onClick={() => editNew(s)} className="btn-dark" style={{ width: "100%", marginBottom: "0.4rem", justifyContent: "flex-start", fontSize: "0.72rem" }}>{s.name}</button>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: "1.6rem" }}>
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.7rem" }}>
            <input className="field" data-testid="template-name" placeholder="Template name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <select className="field" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {["General", "Deal", "Update", "Welcome"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <input className="field" data-testid="template-subject" placeholder="Email subject line" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          <textarea className="field" data-testid="template-html" placeholder="HTML email body…" value={f.html} onChange={(e) => setF({ ...f, html: e.target.value })} rows={14} style={{ fontFamily: "monospace", fontSize: "0.78rem", resize: "vertical" }} />

          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
            <button className="btn-gold" data-testid="save-template-btn" onClick={save}><Save size={14} /> {sel ? "Update" : "Save"} Template</button>
            <button className="btn-dark" data-testid="preview-template-btn" onClick={() => setPreview(true)}><Eye size={14} /> Preview</button>
            <div style={{ flex: 1 }} />
            <select className="field" data-testid="recipient-group" value={group} onChange={(e) => setGroup(e.target.value)} style={{ width: "auto" }}>
              <option value="subscribers">Subscribers only</option>
              <option value="all">All contacts (subs + leads + partners)</option>
            </select>
            <button className="btn-gold" data-testid="send-campaign-btn" disabled={sending} onClick={send}><Send size={14} /> {sending ? "Sending…" : "Send Now"}</button>
          </div>
        </div>
      </div>

      {preview && (
        <div onClick={() => setPreview(false)} data-testid="preview-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 8, width: "100%", maxWidth: 680, maxHeight: "85vh", overflow: "auto", position: "relative" }}>
            <button onClick={() => setPreview(false)} style={{ position: "absolute", top: 12, right: 12, background: "#000", color: "#fff", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer" }}><X size={16} /></button>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #eee", color: "#333", fontFamily: "sans-serif" }}><b>Subject:</b> {f.subject || "(no subject)"}</div>
            <div dangerouslySetInnerHTML={{ __html: f.html || "<p style='padding:20px;color:#999'>Nothing to preview</p>" }} />
          </div>
        </div>
      )}
    </div>
  );
}
